import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Row, Col, Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { useClickEngagement } from '../../services/api/hooks/useClickEngagement';
import { useCampaigns } from '../../services/api/hooks/useCampaigns';
import { useCampaignTags } from '../../services/api/hooks/useCampaignTags';
import { ClickEngagementFilters, ClickEngagementRow } from '../../services/api/types';

interface FilterOption {
  value: string;
  label: string;
}

const DEFAULT_LIMIT = 50;

const toIsoUtc = (d: string, end: boolean = false): string => {
  if (!d) return '';
  const suffix = end ? 'T23:59:59.000Z' : 'T00:00:00.000Z';
  return d + suffix;
};

const todayUtcDate = (): string => new Date().toISOString().slice(0, 10);

const monthAgoUtcDate = (): string => {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 10);
};

interface CampaignLike {
  id: number | string;
  tag?: string | null;
  name?: string | null;
}

const ClickEngagement: React.FC = () => {
  const { data, loading, error, exporting, fetchEngagement, exportCsv } = useClickEngagement();
  const { data: campaignsData } = useCampaigns();
  const { tags: campaignTagOptions } = useCampaignTags();
  const [startDate, setStartDate] = useState<string>(monthAgoUtcDate());
  const [endDate, setEndDate] = useState<string>(todayUtcDate());
  const [search, setSearch] = useState<string>('');
  const [campaignIds, setCampaignIds] = useState<string[]>([]);
  const [campaignTags, setCampaignTags] = useState<string[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [limit] = useState<number>(DEFAULT_LIMIT);
  const [applied, setApplied] = useState<ClickEngagementFilters | null>(null);

  const campaigns: CampaignLike[] = useMemo<CampaignLike[]>(
    () => (Array.isArray(campaignsData) ? (campaignsData as CampaignLike[]) : []),
    [campaignsData]
  );

  const campaignOptions = useMemo(
    () =>
      campaigns.map((c) => ({
        value: String(c.id),
        label: c.name || c.tag || `Campaign ${c.id}`,
      })),
    [campaigns]
  );

  const campaignTagOptionsList = useMemo(
    () =>
      (campaignTagOptions || []).map((tag) => ({ value: tag, label: tag })),
    [campaignTagOptions]
  );

  const runQuery = (next: ClickEngagementFilters): void => {
    setApplied(next);
    fetchEngagement(next).catch(() => undefined);
  };

  const getBaseFilters = (): ClickEngagementFilters => ({
    startDate: startDate ? toIsoUtc(startDate, false) : undefined,
    endDate: endDate ? toIsoUtc(endDate, true) : undefined,
    search: search || undefined,
    campaignId: campaignIds.length > 0 ? campaignIds : undefined,
    campaignTag: campaignTags.length > 0 ? campaignTags : undefined,
    domain: domains.length > 0 ? domains : undefined,
  });

  const handleGenerate = (): void => {
    runQuery({ ...getBaseFilters(), page: 1, limit });
  };

  const handleExport = async (): Promise<void> => {
    try {
      await exportCsv(getBaseFilters());
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handlePageChange = (newPage: number): void => {
    if (!applied) return;
    runQuery({ ...applied, page: newPage, limit });
  };
  const total = data?.total ?? 0;
  const page = data?.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const rows: ClickEngagementRow[] = data?.rows ?? [];

  return (
    <div className="mt-3">
      <p className="text-muted small">Dates and times are UTC. Shows individual click events across all campaigns.</p>

      <Form>
        <Row className="g-3 align-items-end">
          <Col md={2}>
            <Form.Label>Start Date (UTC)</Form.Label>
            <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label>End Date (UTC)</Form.Label>
            <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label>Search</Form.Label>
            <Form.Control
              type="text"
              placeholder="Email, campaign tag, URL"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Label>Campaign</Form.Label>
            <Select<FilterOption, true>
              options={campaignOptions}
              value={campaignOptions.filter((o) => campaignIds.includes(o.value))}
              onChange={(opt) => setCampaignIds(opt.map((o) => String(o.value)))}
              isMulti
              isClearable
              isSearchable
              className="basic-single"
              classNamePrefix="select"
              placeholder="All campaigns"
            />
          </Col>
          <Col md={3}>
            <Form.Label>Campaign Tag</Form.Label>
            <Select<FilterOption, true>
              options={campaignTagOptionsList}
              value={campaignTagOptionsList.filter((o) => campaignTags.includes(o.value))}
              onChange={(opt) => setCampaignTags(opt.map((o) => String(o.value)))}
              isMulti
              isClearable
              isSearchable
              className="basic-single"
              classNamePrefix="select"
              placeholder="All tags"
            />
          </Col>
          <Col md={3}>
            <Form.Label>Domain</Form.Label>
            <CreatableSelect<FilterOption, true>
              options={[]}
              value={domains.map((d) => ({ value: d, label: d }))}
              onChange={(opt) => setDomains(opt.map((o) => String(o.label || o.value)))}
              isMulti
              isClearable
              isSearchable
              placeholder="Type or select domains"
              className="basic-single"
              classNamePrefix="select"
            />
          </Col>
          <Col md={3} className="d-flex align-items-center gap-2">
            {data && (
              <>
                <span className="text-muted">Total Clicks:</span>
                <span
                  className="fw-bold fs-5"
                  data-testid="click-engagement-total-count"
                  aria-live="polite"
                >
                  {data.total.toLocaleString()}
                </span>
              </>
            )}
          </Col>
          <Col md={6} className="d-flex justify-content-end gap-2">
            <Button variant="primary" onClick={handleGenerate} disabled={loading}>
              {loading ? 'Loading...' : 'Generate Report'}
            </Button>
            <Button variant="outline-secondary" onClick={handleExport} disabled={exporting}>
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Col>
        </Row>
      </Form>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading click engagement...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <div className="shadow rounded overflow-hidden mt-4">
            <Table striped bordered hover responsive className="mb-0">
              <thead style={{ backgroundColor: '#0d6efd', color: 'white' }}>
                <tr>
                  <th>Date/Time (UTC)</th>
                  <th>Email</th>
                  <th>Campaign Name</th>
                  <th>Domain</th>
                  <th>URL</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No click data for selected filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={`${r.dateTime}-${r.email}-${r.url}`}>
                      <td>{r.dateTime ? new Date(r.dateTime).toLocaleString() : '-'}</td>
                      <td>{r.email}</td>
                      <td>{r.campaignName || '-'}</td>
                      <td>{r.domain || '-'}</td>
                      <td>
                        <span title={r.url} style={{ wordBreak: 'break-all' }}>
                          {r.url}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">
              Total rows: {total.toLocaleString()} | Page {page} of {totalPages}
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => handlePageChange(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClickEngagement;
